import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
    IconButton, InputAdornment, makeStyles, Theme, Typography,
} from '@material-ui/core';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faQrcode, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { AutocompleteChangeReason } from '@material-ui/lab/Autocomplete/Autocomplete';
import { MobileDatePicker } from '@material-ui/pickers';
import moment from 'moment/moment';

import { Page } from '../../components/Page';
import { BackAppBar } from '../../components/BackAppBar';
import { PageBody } from '../../components/PageBody';
import { FormGroup } from '../../components/FormGroup';
import { Select } from '../../components/Select';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { MemberContactData, MemberContactForm } from './components/MemberContactForm';
import { RepairDraftRequestData } from '../../api/repairDraft';
import { api } from '../../api';
import { useLoading } from '../../units/loading';
import { useAlert } from '../../units/alert';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { useReasonList } from '../../hooks/useReason';
import { usePartner } from '../../hooks/usePartner';
import { GuestContactForm, GuestContactFormData } from './components/GuestContactForm';
import { useQuery } from '../../units/useQuery';
import { Autocomplete } from '../../components/Autocomplete';
import { useProductList } from '../../hooks/useProductList';
import { useLiff } from '../../units/liff';
import { Block } from '../../components/Block';
import { FileUploadButton } from './components/FileUploadButton';

type RepairPageProps = {};

RepairPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        '& .MuiPickersDateRangePickerInput-root': {
            margin: theme.spacing(1),
        },
    },
}));

function useFormData() {
    const emptyFormData: RepairDraftRequestData = {
        login: '',
        line_uid: '',
        location_bef: '',
        barcode_bef: '',
        sn_code_id_bef: '',
        product_model_id_bef: '',
        reason_ids_bef: '',
        reason_bef: '',
        extra_file: '',
        partner_user_id_bef: '',
        customer_vat_bef: '',
        customer_department_id_bef: '',
        customer_main_contact_id_bef: '',
        address_bef: '',
        contact_id_bef: '',
        contact_phone_bef: '',
        contact_email_bef: '',
        repair_date_wish_bef: false,
    };
    const [formData, setFormData] = useState<RepairDraftRequestData>(emptyFormData);

    function clearFormData() {
        setFormData(emptyFormData);
    }

    return {
        formData,
        setFormData,
        clearFormData,
    };
}

function RepairPage(props: RepairPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setIsLoading } = useLoading();
    const query = useQuery();
    const alert = useAlert();
    const { reasonList } = useReasonList();
    const { formData, setFormData, clearFormData } = useFormData();
    const { partner, isLoading: isPartnerLoading, partnerLineUid } = usePartner();
    const { productList } = useProductList({
        line_uid: partnerLineUid || '',
        login: partner?.email || '',
    });
    const [selectedReasonList, setSelectedReasonList] = useState<Array<string>>([]);
    const { liff, isReady } = useLiff();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);

    useEffect(() => {
        setIsLoading(isPartnerLoading);
    }, [isPartnerLoading]);

    useEffect(() => {
        if (!isPartnerLoading && !partner) {
            loginRequest(() => {});
        }

        function loginRequest(onCancel: () => void) {
            alert.show({
                type: 'waring',
                message: '您尚未登入會員，是否要登入會員?',
                onConfirm: () => {
                    history.replace('/login?redirect=/repair');
                },
                onCancel,
                cancelButton: true,
            });
        }
    }, [partner, isPartnerLoading]);

    useEffect(() => {
        setFormData({
            ...formData,
            barcode_bef: query.get('barcode') || '',
            product_model_id_bef: query.get('product_model_id') || '',
            sn_code_id_bef: query.get('sn_code') || '',
        });
    }, [query]);

    useEffect(() => {
        if (!partner) {
            return;
        }

        setPartnerData();
    }, [partner]);

    function canScanCode() {
        return !isPartnerLoading && !query.get('barcode') && liff.isInClient() && isReady;
    }

    function scanCode() {
        if (!canScanCode()) {
            return;
        }

        liff.scanCodeV2()
            .then(({ value }) => {
                setFormData((prevState) => ({
                    ...prevState,
                    barcode_bef: value || '',
                }));

                const product = productList.find((p) => p.barcode === value);
                if (product) {
                    setFormData((prevState) => ({
                        ...prevState,
                        product_model_id_bef: product.product_model_id,
                        sn_code_id_bef: product.sn_code,
                    }));
                }
            })
            .catch((e) => {
                alert.show({
                    type: 'error',
                    title: '錯誤',
                    message: `${e.code} ${e.message}`,
                });
            });
    }

    function handleScanBarcodeClick() {
        scanCode();
    }

    function resetFormData() {
        clearFormData();
        setSelectedReasonList([]);
        setPartnerData();
        setSelectedFile(null);
        setSelectedDate(null);
    }

    function setPartnerData() {
        if (!partner) {
            return;
        }

        setFormData((prevState) => ({
            ...prevState,
            login: partner.email,
            line_uid: partnerLineUid || '',
            partner_user_id_bef: partner.company_id,
            customer_vat_bef: partner.vat,
            customer_department_id_bef: partner.customer_department,
            customer_main_contact_id_bef: partner.id,
            address_bef: partner.contact_address,
            contact_id_bef: partner.name,
            contact_phone_bef: partner.phone || '',
            contact_email_bef: partner.email,
        }));
    }

    function handleMemberContactDataChange(data: MemberContactData) {
        setFormData({
            ...formData,
            address_bef: data.contactAddress,
            contact_id_bef: data.contactId,
            contact_phone_bef: data.contactPhone,
            contact_email_bef: data.contactEmail,
        });
    }

    function handleGuestContactChange(data: GuestContactFormData) {
        setFormData({
            ...formData,
            address_bef: data.address,
            contact_email_bef: data.contactEmail,
            contact_id_bef: data.contactId,
            contact_phone_bef: data.contactPhone,
            customer_department_id_bef: data.customerDepartmentId,
            customer_vat_bef: data.customerVat,
            partner_user_id_bef: data.partnerUserId,
        });
    }

    function handleBarcodeChange(value: string, reason?: AutocompleteChangeReason | 'change') {
        setFormData((prevState) => ({
            ...prevState,
            barcode_bef: value,
        }));

        if (reason === 'select-option') {
            const product = productList.find((p) => p.barcode === value);
            if (product) {
                setFormData((prevState) => ({
                    ...prevState,
                    product_model_id_bef: product.product_model_id,
                    sn_code_id_bef: product.sn_code,
                }));
            }
        }
    }

    function handleSubmitClick() {
        if (!formData.barcode_bef && !formData.product_model_id_bef) {
            alert.show({
                type: 'error',
                title: '錯誤',
                message: '產品辨識碼或型號需擇一輸入',
            });
            return;
        }

        alert.show({
            type: 'waring',
            title: '操作確認',
            message: '報修資料是否已確認無誤，確認送出？',
            onConfirm: () => repair(),
            cancelButton: true,
        });

        function repair() {
            setIsLoading(true);
            api.repairDraft({
                ...formData,
                reason_ids_bef: selectedReasonList.join(','),
            })
                .then((resData) => {
                    setIsLoading(false);
                    alert.show({
                        type: 'success',
                        title: '操作成功',
                        message: `已成功送出報修單\n您的報修單號為:${resData.name}`,
                        onConfirm: () => {
                            resetFormData();
                            if (liff.isInClient() && isReady) {
                                liff.closeWindow();
                            }
                        },
                    });
                })
                .catch(async (e) => {
                    setIsLoading(false);
                    if (e instanceof StatusFalseError) {
                        const errorData = await e.response.json();
                        alert.show({
                            type: 'error',
                            title: '錯誤',
                            message: errorData.msg,
                        });
                    }
                });
        }
    }

    function handleFileSelect(files: FileList | null) {
        if (!files || !files[0]) {
            return;
        }

        const maxFileSize = 128 * 1024 * 1024;
        if (files[0].size > maxFileSize) {
            alert.show({
                type: 'error',
                title: '錯誤',
                message: '上傳的檔案大小上限為128MB',
            });
            return;
        }

        setIsLoading(true);
        api.files(files[0])
            .then((filePath) => {
                setIsLoading(false);
                setFormData((prevState) => ({
                    ...prevState,
                    extra_file: filePath,
                }));
                setSelectedFile(files[0]);
            })
            .catch(async (e) => {
                console.log(e);
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
    }

    function handleFileRemove() {
        setFormData((prevState) => ({
            ...prevState,
            extra_file: '',
        }));
        setSelectedFile(null);
    }

    function handleDateChange(date: moment.Moment|null) {
        setSelectedDate(date as moment.Moment);
        setFormData((prevState) => ({
            ...prevState,
            repair_date_wish_bef: date ? date.format('YYYY-MM-DD') : false,
        }));
    }

    function canSubmit() {
        return selectedReasonList.length > 0
            && formData.contact_id_bef
            && formData.contact_phone_bef
            && formData.contact_email_bef
            && formData.partner_user_id_bef
            && (partner ? true : formData.customer_department_id_bef);
    }

    return (
        <Page className={classes.root}>
            <BackAppBar title="線上報修" />
            <PageBody>
                <FormGroup
                    title="維修產品"
                    actionElement={(
                        <div style={{ display: 'flex', flexDirection: 'column', marginRight: 8 }}>
                            <Typography variant="caption"><span style={{ color: '#ef5350' }}> * </span>為必填項目</Typography>
                            <Typography variant="caption">請輸入正確資訊以提供完善服務</Typography>
                        </div>
                    )}
                >
                    <Autocomplete
                        options={productList.map((item) => ({ text: `${item.barcode} - ${item.product_model_id} - ${item.sn_code}`, value: item.barcode }))}
                        label="產品辨識碼"
                        freeSolo
                        onChange={handleBarcodeChange}
                        value={formData.barcode_bef}
                        startAdornment={canScanCode() ? (
                            <InputAdornment position="start">
                                <IconButton onClick={handleScanBarcodeClick}>
                                    <Icon icon={faQrcode} />
                                </IconButton>
                            </InputAdornment>
                        ) : null}
                    />
                    <Typography variant="caption" style={{ marginLeft: 8, whiteSpace: 'nowrap' }}>
                        /
                        <span style={{ marginLeft: 8, color: '#cf8755' }}>*產品辨識碼與型號擇一輸入即可</span>
                    </Typography>
                    <TextField
                        label="型號"
                        value={formData.product_model_id_bef}
                        onChange={(e) => setFormData({
                            ...formData,
                            product_model_id_bef: e.target.value,
                        })}
                    />
                    <TextField
                        label="序號"
                        value={formData.sn_code_id_bef}
                        onChange={(e) => setFormData({
                            ...formData,
                            sn_code_id_bef: e.target.value,
                        })}
                    />
                </FormGroup>
                <FormGroup title="維修問題">
                    <MobileDatePicker
                        label="希望收件日期"
                        value={selectedDate}
                        onChange={handleDateChange}
                        inputFormat="YYYY-MM-DD"
                        minDate={moment().format('YYYY-MM-DD')}
                        clearable
                        renderInput={(inputProps) => (<TextField {...inputProps} />)}
                    />
                    <Select
                        labelId="input-reason-code"
                        label="問題分類"
                        options={reasonList.filter((reason) => reason.name).map((reason) => ({
                            text: reason.name,
                            value: reason.id,
                        }))}
                        value={selectedReasonList}
                        onChange={(e) => setSelectedReasonList(e.target.value as string[])}
                        multiple
                        required
                    />
                    <TextField
                        label="問題簡述/備註"
                        value={formData.reason_bef}
                        onChange={(e) => setFormData({
                            ...formData,
                            reason_bef: e.target.value,
                        })}
                    />
                    {selectedFile ? (
                        <Block row alignItems="center" style={{ marginLeft: 8 }}>
                            <IconButton style={{ width: 48, height: 48 }} onClick={handleFileRemove}>
                                <Icon icon={faTimes} />
                            </IconButton>
                            <Typography style={{ marginLeft: 8 }}>{selectedFile.name}</Typography>
                        </Block>
                    ) : null}
                    <FileUploadButton
                        onFileSelect={handleFileSelect}
                        fileAccept="video/*,image/*,.zip,.rar,.7z"
                    />
                    <Typography variant="caption" style={{ marginLeft: 8, whiteSpace: 'nowrap' }}>
                        <span style={{ marginLeft: 8, color: '#cf8755' }}>*只能選擇一個檔案 & 容量上限128MB上傳;</span>
                        <br />
                        <span style={{ marginLeft: 12, color: '#cf8755' }}>可支援影片或是壓縮檔方式上傳。</span>
                    </Typography>
                </FormGroup>
                {partner ? (
                    <MemberContactForm
                        data={{
                            contactAddress: formData.address_bef,
                            contactId: formData.contact_id_bef,
                            contactPhone: formData.contact_phone_bef,
                            contactEmail: formData.contact_email_bef,
                        }}
                        onChange={handleMemberContactDataChange}
                    />
                ) : (
                    <GuestContactForm
                        data={{
                            address: formData.address_bef,
                            contactEmail: formData.contact_email_bef,
                            contactId: formData.contact_id_bef,
                            contactPhone: formData.contact_phone_bef,
                            customerDepartmentId: formData.customer_department_id_bef,
                            customerVat: formData.customer_vat_bef,
                            partnerUserId: formData.partner_user_id_bef,
                        }}
                        onChange={handleGuestContactChange}
                    />
                )}
                <FormGroup>
                    <Button onClick={handleSubmitClick} disabled={!canSubmit()}>確認報修</Button>
                </FormGroup>
            </PageBody>
        </Page>
    );
}

export { RepairPage };
